
import React, { useState, useEffect } from 'react';
import { User, Bell, Shield, Palette, HelpCircle, ChevronRight, Mail, Copy, Check, GitBranch, Server, Code, ExternalLink, Eye, EyeOff, Save, Loader2, Globe, CreditCard, Download } from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { VERSION_INFO, getFullVersionString, getBuildTimestamp } from '../../config/version';
import { getLogoUrl } from '../../shared/utils/imageUtils';
import { useAuthContext } from '../../components/auth/AuthProvider';
import { getUserService } from '../../services/userService';
import { toast } from 'sonner';

const SettingsPage: React.FC = () => {
  // Tabs
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'security' | 'billing' | 'preferences' | 'about'>('profile');

  // Auth Context
  const { user } = useAuthContext();

  // State
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  // Profile State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Security State
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Preferences State (Persisted in localStorage)
  const [preferences, setPreferences] = useState({
    emailNotifications: localStorage.getItem('settings_email_notifications') !== 'false',
    pushNotifications: localStorage.getItem('settings_push_notifications') === 'true',
    theme: localStorage.getItem('settings_theme') || 'Light',
    language: localStorage.getItem('settings_language') || 'English'
  });

  // Initialize profile data from auth context
  useEffect(() => {
    if (user && user.profile) {
      setProfileData({
        firstName: user.profile.given_name || '',
        lastName: user.profile.family_name || '',
        email: user.profile.email || '',
      });
    }
  }, [user]);

  // Save preferences to localStorage whenever changed
  useEffect(() => {
    localStorage.setItem('settings_email_notifications', String(preferences.emailNotifications));
    localStorage.setItem('settings_push_notifications', String(preferences.pushNotifications));
    localStorage.setItem('settings_theme', preferences.theme);
    localStorage.setItem('settings_language', preferences.language);
  }, [preferences]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      const userService = getUserService();
      // Note: We need the ID from the profile 'sub' claim
      if (user?.profile?.sub) {
        await userService.updateUser({
          id: user.profile.sub,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email
        });
        toast.success('Profile updated successfully');
      } else {
        toast.error('Could not identify user');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const userService = getUserService();
      if (user?.profile?.sub) {
        await userService.resetPassword(user.profile.sub, passwordData.newPassword);
        toast.success('Password updated successfully');
        setPasswordData({ newPassword: '', confirmPassword: '' });
      } else {
        toast.error('Could not identify user');
      }
    } catch (error) {
      console.error('Update password error:', error);
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const versionString = getFullVersionString();
  const buildInfo = VERSION_INFO.build;

  const tabs = [
    { key: 'profile', label: 'Profile', icon: <User size={18} /> },
    { key: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { key: 'security', label: 'Security', icon: <Shield size={18} /> },
    { key: 'billing', label: 'Billing', icon: <CreditCard size={18} /> },
    { key: 'preferences', label: 'Preferences', icon: <Palette size={18} /> },
    { key: 'about', label: 'About', icon: <HelpCircle size={18} /> },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-2xl">
                    {profileData.firstName?.[0] || user?.profile?.preferred_username?.[0] || 'U'}
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">
                      Username: <span className="font-medium text-gray-900">{user?.profile?.preferred_username || 'Unknown'}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      ID: {user?.profile?.sub || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleUpdateProfile}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-medium text-gray-900">Email Notifications</h4>
                      <p className="text-sm text-gray-500">Receive email updates about your account activity</p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, emailNotifications: !preferences.emailNotifications })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.emailNotifications ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-medium text-gray-900">Push Notifications</h4>
                      <p className="text-sm text-gray-500">Receive push notifications in your browser</p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, pushNotifications: !preferences.pushNotifications })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.pushNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.pushNotifications ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Password</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleUpdatePassword}
                      disabled={loading || !passwordData.newPassword}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : 'Update Password'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex gap-3">
                <Shield className="text-blue-600 shrink-0" size={20} />
                <div>
                  <h4 className="font-medium text-blue-900 text-sm">Security Note</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Two-factor authentication (2FA) and active session management are currently managed by your identity provider administrator.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Current Plan</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">Professional Plan</h4>
                    <p className="text-gray-600">$49/month</p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Active</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Unlimited processes</p>
                  <p>• Up to 100 users</p>
                  <p>• Advanced analytics</p>
                  <p>• Priority support</p>
                </div>
                <button className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
                  Upgrade Plan
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Method</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center">
                      <CreditCard size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">•••• •••• •••• 4242</p>
                      <p className="text-xs text-gray-500">Expires 12/24</p>
                    </div>
                  </div>
                  <button className="text-sm text-indigo-600 hover:text-indigo-700">Update</button>
                </div>
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Add Payment Method
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Billing History</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-3">
                  {[
                    { date: 'Nov 1, 2024', amount: '$49.00', status: 'Paid' },
                    { date: 'Oct 1, 2024', amount: '$49.00', status: 'Paid' },
                    { date: 'Sep 1, 2024', amount: '$49.00', status: 'Paid' },
                  ].map((invoice, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{invoice.date}</p>
                        <p className="text-xs text-gray-500">Professional Plan</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-900">{invoice.amount}</span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{invoice.status}</span>
                        <button className="text-sm text-indigo-600 hover:text-indigo-700">
                          <Download size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Appearance</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                    <select
                      value={preferences.theme}
                      onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option>Light</option>
                      <option>Dark</option>
                      <option>System</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <select
                      value={preferences.language}
                      onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option>English</option>
                      <option>Spanish</option>
                      <option>French</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">About DoIT v2</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <img
                        src={getLogoUrl()}
                        alt="DoIT Logo"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{VERSION_INFO.app.fullName}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>{versionString}</span>
                        <button
                          onClick={() => copyToClipboard(versionString)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title="Copy version"
                        >
                          {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {VERSION_INFO.app.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Author:</span>
                      <span className="ml-2 text-gray-900">{VERSION_INFO.app.author}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">License:</span>
                      <span className="ml-2 text-gray-900">{VERSION_INFO.app.license}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <GitBranch size={20} className="text-blue-600" />
                Build Information
              </h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Build Number</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {buildInfo.number}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Build Date</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {getBuildTimestamp()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Commit</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {buildInfo.commit.substring(0, 8)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Branch</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {buildInfo.branch}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Code size={20} className="text-purple-600" />
                Technology Stack
              </h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">React</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      v{VERSION_INFO.stack.react}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">TypeScript</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      v{VERSION_INFO.stack.typescript}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Vite</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      v{VERSION_INFO.stack.vite}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Tailwind CSS</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      v{VERSION_INFO.stack.tailwind}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Node</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {VERSION_INFO.stack.node}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Server size={20} className="text-green-600" />
                Environment
              </h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Mode</span>
                    <span className={`text-sm font-mono px-2 py-1 rounded ${VERSION_INFO.environment.isProduction
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {VERSION_INFO.environment.mode.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Base URL</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {VERSION_INFO.environment.baseUrl}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Support</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="space-y-4">
                  <a href="#" className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <HelpCircle size={20} className="text-gray-400" />
                      <span className="text-sm text-gray-900">Help Center</span>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </a>
                  <a href="#" className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <Mail size={20} className="text-gray-400" />
                      <span className="text-sm text-gray-900">Contact Support</span>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </a>
                  <a
                    href={VERSION_INFO.app.repository}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <Globe size={20} className="text-gray-400" />
                      <span className="text-sm text-gray-900">Repository</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ExternalLink size={14} className="text-gray-400" />
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-8 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <span>Settings</span>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your account settings and preferences</p>
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Settings Tabs */}
            <div className="w-64 bg-white border-r border-gray-200 p-6 flex-shrink-0 mb-auto">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab.key
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Settings Content */}
            <div className="flex-1 overflow-auto bg-gray-50/30">
              <div className="max-w-4xl p-8">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
